import React, { useMemo } from 'react';
import { Badge, Card, Col, Row, Stack } from 'react-bootstrap';
import {
  FaCarSide,
  FaClock,
  FaFlagCheckered,
  FaMapMarkerAlt,
  FaRoute,
  FaUserShield,
  FaWalking,
} from 'react-icons/fa';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import { computeRouteMetrics, resolveRoutePoints } from 'features/Responder/features/Tours/utils/routeMath';

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="border rounded p-2 h-100 d-flex gap-2 align-items-center">
    {Icon ? <Icon className="text-primary flex-shrink-0" /> : null}
    <div>
      <div className="text-muted text-uppercase small">{label}</div>
      <div className="fw-semibold">{value ?? '—'}</div>
    </div>
  </div>
);

const toSeconds = (val) => {
  if (Number.isFinite(val)) return val;
  if (typeof val === 'string') {
    const numeric = parseFloat(val.replace('s', ''));
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'Not computed';
  if (seconds < 90) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) return `${mins}m`;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatDistance = (meters) => {
  if (!Number.isFinite(meters) || meters <= 0) return 'Not computed';
  const miles = meters * 0.000621371;
  if (miles < 0.1) return `${miles.toFixed(2)} mi`;
  if (miles < 10) return `${miles.toFixed(2)} mi`;
  return `${miles.toFixed(1)} mi`;
};

const pickStopLabel = (stop, fallback) => {
  if (!stop) return fallback;
  return stop.name || stop.label || stop.address || fallback;
};

const titleize = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const normalizeMode = (value) => {
  if (!value) return null;
  const mode = String(value).trim().toLowerCase();
  if (!mode) return null;
  if (mode.startsWith('walk')) return 'walking';
  if (mode.startsWith('drive')) return 'driving';
  if (mode.startsWith('indoor')) return 'indoor';
  return mode;
};

const summarizeModes = (modes = []) => {
  const unique = new Set(modes.filter(Boolean));
  if (!unique.size) return null;
  if (unique.size === 1) return Array.from(unique)[0];
  if (unique.has('driving') && unique.has('walking')) return 'hybrid';
  return 'hybrid';
};

export default function TourTelemetry({ scrollHeight = null, preferBuilderData = false }) {
  const {
    activeTourData,
    builderTourData,
    routeData,
    checkpoints,
    travelMode,
    stops,
    startLocation,
  } = useTours();
  const hasStartLocation = useMemo(() => {
    const address = startLocation?.address?.trim();
    const placeId = startLocation?.placeId?.trim();
    const lat = Number(startLocation?.lat);
    const lng = Number(startLocation?.lng);
    return Boolean(address || placeId || (Number.isFinite(lat) && Number.isFinite(lng)));
  }, [startLocation]);
  const telemetryTour = preferBuilderData && builderTourData ? builderTourData : activeTourData;
  const builderStops = useMemo(() => {
    if (!preferBuilderData) return null;
    const list = [];
    if (hasStartLocation) {
      list.push({
        type: 'origin',
        name: startLocation?.address || 'Start',
        label: 'Start',
        address: startLocation?.address || '',
      });
    }
    if (Array.isArray(checkpoints) && checkpoints.length) {
      list.push(...checkpoints);
    }
    return list;
  }, [checkpoints, hasStartLocation, preferBuilderData, startLocation]);
  const isActiveTelemetry = preferBuilderData
    ? Boolean(
        builderTourData ||
        routeData ||
        (Array.isArray(checkpoints) && checkpoints.length) ||
        hasStartLocation,
      )
    : Boolean(activeTourData);

  const routePoints = useMemo(
    () => resolveRoutePoints(routeData, telemetryTour),
    [routeData, telemetryTour],
  );

  const legs = useMemo(() => {
    if (Array.isArray(routeData?.legs) && routeData.legs.length) return routeData.legs;
    if (Array.isArray(telemetryTour?.legs) && telemetryTour.legs.length) return telemetryTour.legs;
    return [];
  }, [routeData, telemetryTour]);

  const legDistanceMeters = useMemo(
    () =>
      legs.reduce((sum, leg) => {
        const dist = Number.isFinite(leg?.distanceMeters) ? leg.distanceMeters : 0;
        return sum + dist;
      }, 0),
    [legs],
  );

  const legDurationSeconds = useMemo(
    () =>
      legs.reduce((sum, leg) => {
        const secs = toSeconds(leg?.duration);
        return Number.isFinite(secs) ? sum + secs : sum;
      }, 0),
    [legs],
  );

  const polylineDistanceMeters = useMemo(() => {
    if (!routePoints || routePoints.length < 2) return null;
    return computeRouteMetrics(routePoints).total || null;
  }, [routePoints]);

  const totalDistanceMeters = useMemo(() => {
    if (Number.isFinite(routeData?.totalDistanceMeters)) return routeData.totalDistanceMeters;
    if (Number.isFinite(telemetryTour?.totalDistanceMeters))
      return telemetryTour.totalDistanceMeters;
    if (legDistanceMeters > 0) return legDistanceMeters;
    if (polylineDistanceMeters != null && polylineDistanceMeters > 0) return polylineDistanceMeters;
    return null;
  }, [routeData, telemetryTour, legDistanceMeters, polylineDistanceMeters]);

  const travelDurationSeconds = useMemo(() => {
    const fromRoute = toSeconds(routeData?.totalDuration);
    if (Number.isFinite(fromRoute)) return fromRoute;
    const fromTour = toSeconds(telemetryTour?.totalDuration);
    if (Number.isFinite(fromTour)) return fromTour;
    if (legDurationSeconds > 0) return legDurationSeconds;
    return null;
  }, [routeData, telemetryTour, legDurationSeconds]);

  const extraDurationSeconds = useMemo(() => {
    const sourceStops =
      (Array.isArray(stops) && stops.length && stops) ||
      (Array.isArray(telemetryTour?.stops) && telemetryTour.stops.length && telemetryTour.stops) ||
      [];
    return sourceStops.reduce((sum, stop) => {
      const windowSeconds = Number.isFinite(Number(stop?.timeWindowSeconds))
        ? Number(stop.timeWindowSeconds)
        : Number.isFinite(Number(stop?.timeWindowMinutes))
          ? Number(stop.timeWindowMinutes) * 60
          : 0;
      const dwellSeconds = Number.isFinite(Number(stop?.stopTimeSeconds))
        ? Number(stop.stopTimeSeconds)
        : Number.isFinite(Number(stop?.stopTimeMinutes))
          ? Number(stop.stopTimeMinutes) * 60
          : 0;
      return sum + windowSeconds + dwellSeconds;
    }, 0);
  }, [stops, telemetryTour?.stops]);

  const totalDurationSeconds = useMemo(() => {
    if (Number.isFinite(travelDurationSeconds)) return travelDurationSeconds + extraDurationSeconds;
    if (extraDurationSeconds > 0) return extraDurationSeconds;
    return null;
  }, [travelDurationSeconds, extraDurationSeconds]);

  const nodesOrStops = useMemo(() => {
    if (preferBuilderData) {
      if (builderStops?.length) return builderStops;
      if (Array.isArray(builderTourData?.nodes) && builderTourData.nodes.length)
        return builderTourData.nodes;
      if (Array.isArray(builderTourData?.stops) && builderTourData.stops.length)
        return builderTourData.stops;
    }
    if (Array.isArray(telemetryTour?.nodes) && telemetryTour.nodes.length)
      return telemetryTour.nodes;
    if (Array.isArray(telemetryTour?.stops) && telemetryTour.stops.length)
      return telemetryTour.stops;
    if (Array.isArray(checkpoints) && checkpoints.length) return checkpoints;
    return [];
  }, [builderTourData, builderStops, checkpoints, preferBuilderData, telemetryTour]);

  const checkpointCount = useMemo(() => {
    if (preferBuilderData && Array.isArray(checkpoints) && checkpoints.length)
      return checkpoints.length;
    if (!nodesOrStops.length) return 0;
    const typed = nodesOrStops.filter((n) => n?.type === 'checkpoint');
    if (typed.length) return typed.length;
    return Math.max(nodesOrStops.length - 1, 0);
  }, [checkpoints, nodesOrStops, preferBuilderData]);

  const origin = nodesOrStops.find((n) => n?.type === 'origin') || nodesOrStops[0];
  const destination =
    nodesOrStops.find((n) => n?.type === 'destination') ||
    (nodesOrStops.length ? nodesOrStops[nodesOrStops.length - 1] : null);

  const assignedOperator = null; // Placeholder until backend wiring is added

  const travelModeLabel = useMemo(() => {
    const legModes = legs.map((leg) => normalizeMode(leg?.travelMode || leg?.mode));
    const legSummary = summarizeModes(legModes);
    if (legSummary) {
      return legSummary === 'hybrid' ? 'Hybrid' : titleize(legSummary);
    }

    const stopModes = (Array.isArray(checkpoints) ? checkpoints : [])
      .map((cp) => normalizeMode(cp?.mode || cp?.travelMode))
      .filter(Boolean);
    const stopSummary = summarizeModes(stopModes);
    if (stopSummary) {
      return stopSummary === 'hybrid' ? 'Hybrid' : titleize(stopSummary);
    }

    const mode = normalizeMode(telemetryTour?.travelType || travelMode);
    if (mode) return mode === 'hybrid' ? 'Hybrid' : titleize(mode);
    return 'Not set';
  }, [checkpoints, legs, telemetryTour?.travelType, travelMode]);

  const optimizationLabel = titleize(telemetryTour?.routeOptimization || '');

  const containerStyle = scrollHeight
    ? { height: '100%', maxHeight: scrollHeight, overflowY: 'auto' }
    : {};

  return (
    <Card className="shadow-sm h-100" style={containerStyle}>
      <Card.Body className="d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="text-muted text-uppercase small">Tour telemetry</div>
            <div className="fw-semibold fs-5">
              {telemetryTour?.name || (isActiveTelemetry ? 'Draft tour' : 'No active route')}
            </div>
            <div className="small text-muted">
              {isActiveTelemetry
                ? `${checkpointCount || 0} checkpoint${checkpointCount === 1 ? '' : 's'} in tour`
                : 'Select or build a route to see live details.'}
            </div>
          </div>
          <Badge bg={isActiveTelemetry ? 'primary' : 'secondary'}>
            {isActiveTelemetry ? 'Active' : 'Idle'}
          </Badge>
        </div>

        <Row className="g-2">
          <Col xs={12} md={6}>
            <StatTile icon={FaRoute} label="Distance" value={formatDistance(totalDistanceMeters)} />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              icon={FaClock}
              label="Estimated time"
              value={formatDuration(totalDurationSeconds)}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              icon={travelModeLabel === 'Walking' ? FaWalking : FaCarSide}
              label="Mode"
              value={travelModeLabel}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatTile
              icon={FaFlagCheckered}
              label="Route type"
              value={optimizationLabel || 'Not set'}
            />
          </Col>
        </Row>

        <Stack gap={2}>
          <div className="d-flex align-items-center gap-2">
            <FaMapMarkerAlt className="text-success" />
            <div>
              <div className="text-muted text-uppercase small">Start</div>
              <div className="fw-semibold">
                {origin ? pickStopLabel(origin, 'Start not set') : 'Start not set'}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FaMapMarkerAlt className="text-danger" />
            <div>
              <div className="text-muted text-uppercase small">Destination</div>
              <div className="fw-semibold">
                {destination
                  ? pickStopLabel(destination, 'Destination not set')
                  : 'Destination not set'}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FaUserShield className="text-primary" />
            <div>
              <div className="text-muted text-uppercase small">Assigned operator</div>
              <div className="fw-semibold">{assignedOperator ?? 'Unassigned'}</div>
              <div className="small text-muted">Awaiting integration</div>
            </div>
          </div>
        </Stack>
      </Card.Body>
    </Card>
  );
}
